import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { CaretDown } from "@phosphor-icons/react";
import { useGetAnnotations } from "@/utils/react-query-hooks/getData";
import { useGetTopicClusteringTree } from "@/utils/react-query-hooks/aiAnnotation";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { useAggrMetricsAcrossDimensions } from "../WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import {
  useGetTypeCategoryAndTags,
  convertTagDocsDataToOptions,
} from "@/components/inputs/DynamicTagsInputs/utils";
import {
  WidgetErrorState,
  WidgetLoadingState,
} from "../../WidgetRenderer/comps/altStatePlaceholders";
import {
  colors2,
  DropdownSingle,
  Divider,
  getVal,
  Modal,
  setVal,
  SkeletonLoader,
  TextInput,
  UI_BODY_SM,
  UI_BODY_SEMIBOLD_DF,
  UI_BODY_SM_DF,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_TAG,
} from "oolib";
import { extractAllBlocksFromTpl, blockRegistry, TOPIC_NODE_UNPLACED_ID, isParkedSubtopicId } from "@okf/ootils";
import { useQueryClient } from "react-query";
import { WidgetHeader } from "../WidgetBarChart/WIDGET_ELEM_ConfigForm";
import { useUpdateLoadedWidgetsTracker } from "../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { useReportLandingContext } from "../../../ReportLandingContext";
import { StyledCompWrapper } from "../../../styled";
import {
  PrevalenceSections,
  useTopicPrevalence,
  buildSubThemeColorIdxMap,
  groupAnnotationDataBySubTheme,
} from "@/components/analysisModules/comps/AnnotationPrevalence";
import { getDerivedBlockTitle } from "../WidgetBarChart/utils/_extractWidgetTitle";
import { CardAnnoExplorer } from "@/components/analysisModules/AnnotationExplorer/comps/CardAnnoExplorer";
import { WidgetFilters } from "../utils/WidgetFilters";
import { getWidgetContentTypes } from "../utils/getWidgetContentTypes";

// Compact inline trigger for the per-subTheme picker. Mirrors the "WEEK ▼"
// pattern used elsewhere — text + caret, underline accent, no full form
// styling. Wired into DropdownSingle via `genCustomSelectComp`.
const StyledAnnoSubThemeTrigger = styled.button`
  background: none;
  border: 1px solid ${colors2.grey20};
  border-radius: 4px;
  padding: 2.5px 5px;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;

  &:hover {
    background: ${colors2.grey5};
  }
`;

// Rolls each annotation's tags up to their subThemes BE-side, de-duping so an
// annotation counts once per subTheme it touches. Module-level so the query
// body stays referentially stable across renders.
const SUBTHEME_ROLLUP_DIMENSIONS = [
  { rollupType: "tagType", isAnno: true, path: ["tags", "subThemes"] },
];

// Modal content for a clicked Topics bar (split mode). Node membership lives
// on the clustering tree, not on annotation docs — so we read the (cached)
// tree, collect the clicked node's annotation ids, and fetch those
// annotations by _id. Four kinds of id arrive here: a topic (everything
// beneath it, its parked observations included), a sub-topic (its own), a
// topic's parked pseudo sub-topic "__unplaced:<topicId>", and the root
// "__unplaced" (the tree endpoint's unplacedObservations).
const TopicClusterAnnotationsListContent = ({ nodeId, contentType, valuePath, activeFilters, widgetFilters = [] }) => {
  const combinedFilters = useMemo(() => [...activeFilters, ...widgetFilters], [activeFilters, widgetFilters]);
  const { data: treeData, status: treeStatus } = useGetTopicClusteringTree({});

  const annotationIds = useMemo(() => {
    if (!treeData) return [];
    if (nodeId === TOPIC_NODE_UNPLACED_ID) {
      return (treeData.unplacedObservations || []).map((o) => String(o._id));
    }
    const ids = [];
    // The enriched tree carries a topic's parked observations as objects on
    // `parkedObservations`, and a sub-topic's members as `annotationIds`.
    const collectUnder = (node) => {
      for (const id of node.annotationIds || []) ids.push(String(id));
      for (const o of node.parkedObservations || []) ids.push(String(o._id));
      for (const child of node.children || []) collectUnder(child);
    };
    if (isParkedSubtopicId(nodeId)) {
      const topicId = String(nodeId).slice(`${TOPIC_NODE_UNPLACED_ID}:`.length);
      const topic = (treeData.tree || []).find((t) => String(t.nodeId) === topicId);
      return (topic?.parkedObservations || []).map((o) => String(o._id));
    }
    const walk = (nodes) => {
      for (const node of nodes || []) {
        if (String(node.nodeId) === String(nodeId)) collectUnder(node);
        else walk(node.children);
      }
    };
    walk(treeData.tree);
    return ids;
  }, [treeData, nodeId]);

  const { data, status } = useGetAnnotations({
    dataSource: "annotations",
    activeFilters: combinedFilters,
    findQuery: {
      _id: { $in: annotationIds },
      "meta.contentType": contentType,
      "meta.valuePath": valuePath,
    },
    queryOptions: { enabled: annotationIds.length > 0 },
  });

  if (treeStatus === "loading" || status === "loading") {
    return (
      <div style={{ padding: "2rem" }}>
        <SkeletonLoader style={{ height: "100px", marginBottom: "1rem" }} />
        <SkeletonLoader style={{ height: "100px" }} />
      </div>
    );
  }

  if (status === "error" || treeStatus === "error") {
    return <div style={{ padding: "2rem" }}>Error loading annotations</div>;
  }

  const annos = data?.data || [];
  if (!annos.length) {
    return (
      <div style={{ padding: "2rem" }}>
        <UI_BODY_SM style={{ color: colors2.grey60 }}>No annotations found for this topic.</UI_BODY_SM>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        padding: "1rem",
        backgroundColor: colors2.grey5,
      }}
    >
      {annos.map((anno) => (
        <CardAnnoExplorer
          key={anno._id + (anno.annotations?.annoKey || "")}
          cardColor={colors2.grey2}
          singleAnno={anno}
        />
      ))}
    </div>
  );
};

const AnnotationsByTagListContent = ({ tagIds, contentType, valuePath, activeFilters, widgetFilters = [] }) => {
  const combinedFilters = useMemo(() => [...activeFilters, ...widgetFilters], [activeFilters, widgetFilters]);

  const { data, status } = useGetAnnotations({
    dataSource: "annotations",
    activeFilters: combinedFilters,
    findQuery: {
      "annotations.tags.tags.data._id": { $in: tagIds },
      "meta.contentType": contentType,
      "meta.valuePath": valuePath,
    },
    queryOptions: { enabled: tagIds?.length > 0 },
  });

  if (status === "loading") {
    return (
      <div style={{ padding: "2rem" }}>
        <SkeletonLoader style={{ height: "100px", marginBottom: "1rem" }} />
        <SkeletonLoader style={{ height: "100px" }} />
      </div>
    );
  }

  if (status === "error") {
    return <div style={{ padding: "2rem" }}>Error loading annotations</div>;
  }

  const annos = data?.data || [];
  if (!annos.length) {
    return (
      <div style={{ padding: "2rem" }}>
        <UI_BODY_SM style={{ color: colors2.grey60 }}>No annotations found for this tag.</UI_BODY_SM>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        padding: "1rem",
        backgroundColor: colors2.grey5,
      }}
    >
      {annos.map((anno) => (
        <CardAnnoExplorer
          key={anno._id + (anno.annotations?.annoKey || "")}
          cardColor={colors2.grey2}
          singleAnno={anno}
        />
      ))}
    </div>
  );
};

export const Component = ({
  blockValuePath,
  breakdownDisplayType,
  contentType,
  id,
  widgetIdx,
  activeFilters = [],
  widgetFilters = [],
  actions,
  presetActiveFilters = [],
  enableVisibilityFetching = false,
  // Optional breakdown dimension (same shape as aggrMetricsAcrossDimensions).
  // When present, each per-subTheme BarChart renders with breakdown applied.
  breakdownDimension,
  // Optional taxonomy filter.
  //   undefined (default) → show all subThemes (non-experimental "Annotations" mode).
  //   "thematic"          → only subThemes where meta.taxonomyRole !== "attribute".
  //   "attribute"         → only subThemes where meta.taxonomyRole === "attribute".
  // Callers set a concrete role when aiAnnotation.mode is non-legacy; otherwise
  // leave undefined and the widget behaves as the plain pre-experimental single-annotations view.
  taxonomyRole,
  prefetchedData,
  // Host-provided sizing. The reports WidgetRenderer passes every widget
  // { maxHeight: WIDGET_MAX_HEIGHT } — this widget used to drop the prop, so
  // it was the one report widget that grew unbounded. The wrapper already has
  // overflow-y: auto, so honoring it caps + scrolls like the other widgets.
  // Hosts that pass no style (Overview, DataPivot) are unchanged, and the
  // Annotation Explorer's prevalence tab doesn't render this component at all.
  style = {},
}) => {
  const { updateWidgetDataStore } = useReportLandingContext() || {};

  // Snapshot replay: prefetchedData carries `data` — the flat per-tag annotation
  // rows the render path consumes. When present, skip all fetches and render
  // from the captured slice; the FE-side grouping re-derives from `data` +
  // globally-cached tag/subTheme metadata.
  const [selectedBar, setSelectedBar] = useState(null);
  // User can narrow to a single subTheme within the active taxonomy.
  // "__all__" means "show every subTheme in this taxonomy".
  const [selectedSubThemeId, setSelectedSubThemeId] = useState("__all__");

  const combinedFilters = useMemo(
    () => [...activeFilters, ...presetActiveFilters, ...widgetFilters],
    [activeFilters, presetActiveFilters, widgetFilters]
  );

  // Split tenants get a "Topics" section (clustering-tree buckets) INSIDE the
  // annotations view — not a separate mode.
  const platformConfigs = useGetQueryData("platformConfigs");
  const showTopicClustersSection = getAnnoMode(platformConfigs) === "split";

  // ── Annotations (tags grouped by subTheme) ──
  // We query tag counts only (1 dimension, + optional breakdown) against the
  // `annotations` collection, then group by subTheme on the FE using each
  // tag's own `subThemes` metadata.
  //
  // Why not roll up to subTheme server-side: the rollup injects all of an
  // annotation's tags' subThemes into a single flat array, so a 2-dim
  // aggregation (subTheme × tag) becomes a cross-product — an annotation with
  // 4 tags spanning 4 subThemes produces 16 (subTheme, tag) cells, most of
  // them bogus. Per-tag grouping avoids this since each tag has exactly one
  // subTheme (its first listed one).
  const annotationFindQuery = useMemo(
    () => ({ "meta.contentType": contentType, "meta.valuePath": blockValuePath }),
    [contentType, blockValuePath]
  );
  const annotationDimensions = useMemo(() => {
    const base = [{ valuePath: "annotations.tags.tags", isAnno: true }];
    if (breakdownDimension) base.push(breakdownDimension);
    return base;
  }, [breakdownDimension]);
  const {
    data: annotationDataFromApi,
    status: annotationStatus,
    error: annotationError,
    ref,
  } = useAggrMetricsAcrossDimensions({
    vizCardId: `anno-${id || blockValuePath}`,
    contentType: "annotations",
    dimensions: annotationDimensions,
    findQuery: annotationFindQuery,
    activeFilters: combinedFilters,
    queryOptions: { enabled: !prefetchedData },
    enableVisibilityFetching,
  });

  // On snapshot replay, feed the captured raw rows back into the annotation
  // grouping pipeline so `annotationGrouped` re-derives the same view.
  const annotationData = prefetchedData ? prefetchedData.data : annotationDataFromApi;

  // Per-subTheme counts, in annotation units. A subTheme's total can't be
  // summed from its tag counts — an annotation carrying three tags from one
  // subTheme would count three times — so the BE rolls tags up to subThemes
  // and de-dupes per annotation. includePartialDimensions keeps annotations
  // whose tags have no subTheme, under "(N/A)" → "Ungrouped".
  const { data: subThemeCountsFromApi, status: subThemeCountsStatus } = useAggrMetricsAcrossDimensions({
    vizCardId: `annoSubThemes-${id || blockValuePath}`,
    contentType: "annotations",
    dimensions: SUBTHEME_ROLLUP_DIMENSIONS,
    findQuery: annotationFindQuery,
    activeFilters: combinedFilters,
    includePartialDimensions: true,
    queryOptions: { enabled: !prefetchedData },
  });
  const subThemeAnnotationCounts = prefetchedData
    ? prefetchedData.subThemeAnnotationCounts
    : subThemeCountsFromApi;

  // ── Topics sections (split mode) ──
  // One section per topic with its sub-topics as the bars, from the same
  // hook the Annotation Explorer's prevalence panel uses. Fails soft: the
  // sections simply don't render, so they can never block the sub-theme
  // groups. No breakdown support yet.
  //
  // No own visibility observer (its ref would have nowhere to attach — the
  // wrapper carries the annotations query's ref). Instead it chains off the
  // annotations query, which IS visibility-gated: topics fire once the
  // widget's main fetch has landed, so lazy hosts (Overview) stay lazy.
  const { topicGroups, status: topicPrevalenceStatus } = useTopicPrevalence({
    vizCardIdPrefix: `topicClusters-${id || blockValuePath}`,
    findQuery: annotationFindQuery,
    activeFilters: combinedFilters,
    breakdownDimension,
    enabled: showTopicClustersSection && !prefetchedData && annotationStatus === "success",
  });

  // Tag and subTheme metadata for FE-side grouping. react-query caches these globally so
  // they're essentially free on repeat renders. On snapshot replay we skip the fetch (the
  // public page is unauthenticated and can't load them) and use the captured copies instead.
  const tagsQuery = useGetTypeCategoryAndTags({
    tagTypeProp: "tags",
    enableDataFetch: !prefetchedData,
  });
  const subThemesQuery = useGetTypeCategoryAndTags({
    tagTypeProp: "subThemes",
    enableDataFetch: !prefetchedData,
  });
  // Prefer captured metadata on replay; live, use the fetched query data.
  const tagsData = prefetchedData
    ? prefetchedData.tagsData
    : (tagsQuery.status === "success" ? tagsQuery.data : null);
  const subThemesData = prefetchedData
    ? prefetchedData.subThemesData
    : (subThemesQuery.status === "success" ? subThemesQuery.data : null);

  // Map subThemeId → colorIdx, anchored to the global subTheme metadata (not
  // current data), so colors stay put across filter changes.
  const subThemeColorIdxMap = useMemo(() => {
    const subThemeDocs = subThemesData
      ? convertTagDocsDataToOptions(subThemesData, "tags")
      : [];
    return buildSubThemeColorIdxMap(subThemeDocs);
  }, [subThemesData]);

  // Topic sections continue the colour run after the sub-theme slots, so a
  // topic keeps its colour as filters change — same scheme the explorer uses.
  const colorIdxMap = useMemo(() => {
    const map = { ...subThemeColorIdxMap };
    const offset = Object.keys(map).length;
    [...topicGroups]
      .map((g) => g.subThemeId)
      .sort()
      .forEach((id, i) => {
        map[id] = offset + i;
      });
    return map;
  }, [subThemeColorIdxMap, topicGroups]);

  // Group the flat per-tag counts into [{ subThemeId, subThemeDisplay, taxonomyRole, total, percentage, tags: [...] }].
  // Shared with the Annotation Explorer's prevalence panel — this widget used to
  // carry its own copy, which silently kept the bug that copy was written with
  // (section totals summed from tag counts, so one annotation counted once per
  // tag it carried). One grouper, one counting rule: 1 annotation = 1 count.
  const annotationGrouped = useMemo(() => {
    const tagDocs = tagsData ? convertTagDocsDataToOptions(tagsData, "tags") : [];
    const subThemeDocs = subThemesData ? convertTagDocsDataToOptions(subThemesData, "tags") : [];
    const groups = groupAnnotationDataBySubTheme({
      annotationData,
      subThemeAnnotationCounts,
      tagDocs,
      subThemeDocs,
    });
    // Public-share snapshots are persisted server-side, and ones captured
    // before per-subTheme counts existed carry no `subThemeAnnotationCounts` —
    // leaving every section at 0% (0). A snapshot is a frozen record of what
    // was on screen that day, so rebuild the figures it was showing then:
    // totals summed from tag counts, shares against that sum. Live views never
    // take this path, and re-sharing a report captures the correct counts.
    if (prefetchedData && !subThemeAnnotationCounts) {
      const summed = groups.map((g) => ({
        ...g,
        total: (g.tags || []).reduce((sum, t) => sum + (t.count || 0), 0),
      }));
      const denom = summed.reduce((sum, g) => sum + g.total, 0);
      return summed
        .map((g) => ({ ...g, percentage: denom > 0 ? (g.total / denom) * 100 : 0 }))
        .sort((a, b) => b.total - a.total);
    }
    return groups;
  }, [annotationData, subThemeAnnotationCounts, prefetchedData, tagsData, subThemesData]);

  // Narrow by active taxonomyRole (if any) and optional specific-subTheme pick.
  // When taxonomyRole is undefined (non-experimental mode), no taxonomy filtering is applied.
  const visibleAnnotationGroups = useMemo(() => {
    if (!Array.isArray(annotationGrouped)) return [];
    const byRole = taxonomyRole
      ? annotationGrouped.filter((g) => g.taxonomyRole === taxonomyRole)
      : annotationGrouped;
    if (selectedSubThemeId === "__all__") return byRole;
    return byRole.filter((g) => g.subThemeId === selectedSubThemeId);
  }, [annotationGrouped, taxonomyRole, selectedSubThemeId]);

  // Reset the per-subTheme picker and any open drilldown when the user switches taxonomies.
  useEffect(() => {
    setSelectedSubThemeId("__all__");
    setSelectedBar(null);
  }, [taxonomyRole]);

  // `annotationData` was already overridden above on snapshot replay, so the
  // grouping memos see the captured rows too.
  const data = annotationData;
  // We also wait on the tag + subTheme metadata queries and the per-subTheme
  // counts, since the FE grouping needs them — without the last one every
  // section would render "0% (0)" until it lands. Any loading → loading; any
  // error → error; otherwise success.
  const status = prefetchedData
    ? "success"
    : (() => {
        const parts = [annotationStatus, subThemeCountsStatus, tagsQuery.status, subThemesQuery.status];
        if (parts.some((s) => s === "error")) return "error";
        if (parts.some((s) => s === "loading" || s === "idle")) return "loading";
        return "success";
      })();
  const error = annotationError;

  // Push the rendered slice to the central store so a public snapshot can replay
  // it. We also capture the tag/subTheme metadata the FE grouping needs, since the public
  // page can't fetch it. Shape captured here === shape read from prefetchedData.
  useEffect(() => {
    if (status === "success" && data?.length && id) {
      updateWidgetDataStore && updateWidgetDataStore(id, {
        data,
        tagsData: tagsQuery.data,
        subThemesData: subThemesQuery.data,
        subThemeAnnotationCounts,
      });
    }
  }, [status, data, tagsQuery.data, subThemesQuery.data, subThemeAnnotationCounts, id, updateWidgetDataStore]);

  useUpdateLoadedWidgetsTracker({ status, widgetIdx, hasData: !!data?.length, widgetId: id });

  // Each bar is a single tag; barData.value is the tagId.
  const handleBarClick = (barData) => {
    setSelectedBar({ tagIds: [barData.value], display: barData.name });
  };

  // Topics-section bars carry a nodeId (or "__unplaced"), not a tagId —
  // separate handler so the modal knows to resolve via the clustering tree.
  const handleTopicClusterBarClick = (barData) => {
    setSelectedBar({ topicClusterNodeId: barData.value, display: barData.name });
  };

  // One section per subTheme, then one per topic, all through the shared
  // PrevalenceSections so this widget and the Annotation Explorer's
  // prevalence panel render identically. Every bar shows — the widget shell
  // scrolls — rather than folding the tail into an "Others" bar.
  const genViz = () => {
    const roleFilteredGroups = Array.isArray(annotationGrouped)
      ? (taxonomyRole
          ? annotationGrouped.filter((g) => g.taxonomyRole === taxonomyRole)
          : annotationGrouped)
      : [];
    const groups = Array.isArray(visibleAnnotationGroups) ? visibleAnnotationGroups : [];

    const subThemeOptions = [
      { display: "All", value: "__all__" },
      ...roleFilteredGroups.map((g) => ({ display: g.subThemeDisplay, value: g.subThemeId })),
    ];
    const selectedSubThemeDisplay =
      subThemeOptions.find((o) => o.value === selectedSubThemeId)?.display || "All";

    return (
      <div style={{ display: "flex", flexDirection: "column", width: '100%' }}>
        {roleFilteredGroups.length > 1 && (
          <div style={{ padding: "8px 16px", borderTop: `1px solid ${colors2.grey10}` }}>
            <DropdownSingle
              id="annoSubThemePick"
              value={selectedSubThemeId}
              onChange={(_k, v) => setSelectedSubThemeId(v)}
              options={subThemeOptions}
              saveValueAsString
              popOutOfOverflowHiddenParent
              genCustomSelectComp={() => (
                <StyledAnnoSubThemeTrigger>
                  <UI_TAG style={{ color: colors2.grey80 }}>
                    {selectedSubThemeDisplay}
                  </UI_TAG>
                  <CaretDown size={10} weight="bold" color={colors2.grey60} />
                </StyledAnnoSubThemeTrigger>
              )}
            />
          </div>
        )}
        <PrevalenceSections
          groups={groups}
          colorIdxMap={colorIdxMap}
          onBarClick={handleBarClick}
          breakdownDisplayType={breakdownDisplayType}
          sectionPadding="0 16px"
        />
        {/* Topics sections (split mode): one section per topic, its
            sub-topics as the bars, below the sub-theme groups. Fails soft —
            hidden while loading/errored, before a recluster has placed
            anything, or when the user has narrowed to a single sub-theme. */}
        {showTopicClustersSection &&
          selectedSubThemeId === "__all__" &&
          topicPrevalenceStatus === "success" &&
          topicGroups.some((g) => g.subThemeId !== TOPIC_NODE_UNPLACED_ID) && (
            <PrevalenceSections
              groups={topicGroups}
              colorIdxMap={colorIdxMap}
              onBarClick={handleTopicClusterBarClick}
              breakdownDisplayType={breakdownDisplayType}
              sectionPadding="0 16px"
            />
          )}
      </div>
    );
  };

  return (
    <StyledCompWrapper ref={ref} style={style}>
      {status === "loading" ? (
        <div style={{ width: "100%" }}>
          <WidgetLoadingState />
        </div>
      ) : status === "error" ? (
        <WidgetErrorState error={error} colorPreset={"purple"} />
      ) : (
        status === "success" && genViz()
      )}

      {selectedBar?.tagIds && (
        <Modal
          title={`Annotations: ${selectedBar.display}`}
          onClose={() => setSelectedBar(null)}
          desktopWidth="70%"
        >
          <AnnotationsByTagListContent
            tagIds={selectedBar.tagIds}
            contentType={contentType}
            valuePath={blockValuePath}
            activeFilters={[...activeFilters, ...presetActiveFilters]}
            widgetFilters={widgetFilters}
          />
        </Modal>
      )}

      {selectedBar?.topicClusterNodeId && (
        <Modal
          title={`Annotations: ${selectedBar.display}`}
          onClose={() => setSelectedBar(null)}
          desktopWidth="70%"
        >
          <TopicClusterAnnotationsListContent
            nodeId={selectedBar.topicClusterNodeId}
            contentType={contentType}
            valuePath={blockValuePath}
            activeFilters={[...activeFilters, ...presetActiveFilters]}
            widgetFilters={widgetFilters}
          />
        </Modal>
      )}
    </StyledCompWrapper>
  );
};

export const optionDef = {
  display: "Annotation Counts",
  icon: "Bookmark",
  value: "annotations",
  comp: "WidgetAICategorization",
  widgetLabel: "Annotation Counts",
};

export const someReqPropsUnset = (config) => {
  return !config?.props?.blockValuePath || !config?.props?.contentType;
};

export const ConfigForm = ({
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
}) => {
  const dataKeys = {
    title: "props.title",
    blockValuePath: "props.blockValuePath",
    contentType: "props.contentType",
  };

  const queryClient = useQueryClient();
  const allTpls = queryClient.getQueryData("allTpls");

  const widgetType = thisWidgetConfig?.comp || 'WidgetAICategorization';
  const contentTypesForFilters = useMemo(() => {
    return getWidgetContentTypes(thisWidgetConfig, widgetType);
  }, [thisWidgetConfig, widgetType]);

  const contentTypeTpls = allTpls.filter(
    (tpl) => tpl.general.segment === "publishing"
  );

  // Annotation-capable via the registry (instance-aware — honours the per-block
  // kill-switch props.annotations.disable).
  const blockOptions = contentTypeTpls.reduce((acc, tpl) => {
    const blocks = extractAllBlocksFromTpl({ tpl });

    const qualBlocks = blocks
      .filter((block) => blockRegistry.hasCapability(block.comp, "annotation", block))
      .map((block) => ({
        display:
          block.props?.label || block.props?.shortLabel || block.valuePath,
        desc: tpl.general?.content?.title || tpl.kp_content_type,
        value: `${tpl.kp_content_type}|${block.valuePath}`,
        contentType: tpl.kp_content_type,
        blockValuePath: block.valuePath,
      }));

    return [...acc, ...qualBlocks];
  }, []);

  const currentValue = (() => {
    const contentType = getVal(thisWidgetConfig, dataKeys.contentType);
    const blockValuePath = getVal(thisWidgetConfig, dataKeys.blockValuePath);
    return contentType && blockValuePath
      ? `${contentType}|${blockValuePath}`
      : "";
  })();

  const handleBlockSelection = (key, value) => {
    if (!value) return;

    const [contentType, blockValuePath] = value.split("|");

    setConfig((prev) => {
      let thisWidgetModifiedConfig = JSON.parse(
        JSON.stringify(prev[underEditWidgetIdx])
      );

      // Set both contentType and blockValuePath
      thisWidgetModifiedConfig = setVal(
        thisWidgetModifiedConfig,
        dataKeys.contentType,
        contentType
      );
      thisWidgetModifiedConfig = setVal(
        thisWidgetModifiedConfig,
        dataKeys.blockValuePath,
        blockValuePath
      );

      const derived = getDerivedBlockTitle({ contentType, blockValuePath });
      thisWidgetModifiedConfig = setVal(
        thisWidgetModifiedConfig,
        dataKeys.title,
        derived
      );

      const modifiedFullConfig = JSON.parse(JSON.stringify(prev));
      modifiedFullConfig[underEditWidgetIdx] = thisWidgetModifiedConfig;
      return modifiedFullConfig;
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <WidgetHeader icon={"Bookmark"} title={"Annotation Counts"} />

      <TextInput
        id={dataKeys.title}
        label="Widget Title"
        value={
          getVal(thisWidgetConfig, dataKeys.title) ||
          getDerivedBlockTitle({
            contentType: getVal(thisWidgetConfig, dataKeys.contentType),
            blockValuePath: getVal(thisWidgetConfig, dataKeys.blockValuePath),
          })
        }
        onChange={(k, v) => {
          setConfig((prev) => {
            let thisWidgetModifiedConfig = JSON.parse(
              JSON.stringify(prev[underEditWidgetIdx])
            );
            thisWidgetModifiedConfig = setVal(
              thisWidgetModifiedConfig,
              dataKeys.title,
              v
            );
            const modifiedFullConfig = JSON.parse(JSON.stringify(prev));
            modifiedFullConfig[underEditWidgetIdx] = thisWidgetModifiedConfig;
            return modifiedFullConfig;
          });
        }}
      />

      <DropdownSingle
        id="blockSelection"
        label="Select Field"
        placeholder="Choose a field with annotations enabled"
        value={currentValue}
        onChange={handleBlockSelection}
        options={blockOptions}
        saveValueAsString
        popOutOfOverflowHiddenParent
      />

      <Divider style={{ margin: "0 -1.6rem" }}/>

      <WidgetFilters
        widgetConfig={thisWidgetConfig}
        updateWidgetConfig={setConfig}
        widgetIndex={underEditWidgetIdx}
        contentTypesForFilters={contentTypesForFilters}
      />
    </div>
  );
};

export const defaultConfig = undefined;
